"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Validator = void 0;
var ClientErrorUtils = require('./ClientErrorUtils.js');
var validateNip = function (nip) {
    if (typeof nip !== 'string')
        return false;
    nip = nip.replace(/[\ \-]/gi, '');
    var weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    var sum = 0;
    var controlNumber = parseInt(nip.substring(9, 10));
    for (var i = 0; i < weight.length; i++) {
        sum += parseInt(nip.substring(i, i + 1)) * weight[i];
    }
    return sum % 11 === controlNumber;
};
var Validator = /** @class */ (function () {
    function Validator(customDataOptions) {
        if (customDataOptions === void 0) { customDataOptions = {}; }
        this.dataOptions = Object.assign({
            isRequired: function (value) { return !!value; },
            longerThan: function (value, min) { return value.length && value.length > min; },
            shorterThan: function (value, max) { return value.length && value.length < max; },
            greaterThan: function (value, min) { return value > min; },
            smallerThan: function (value, max) { return value < max; },
            oneOf: function (value, options) { return options.includes(value); },
        }, customDataOptions);
        this.dataTypes = {
            shape: this.createDataType('shape'),
            arrayOf: this.createDataType('arrayOf'),
            oneOfType: this.createDataType('oneOfType'),
            string: this.createDataType('string', function (value) {
                var newValue = String(value);
                return { valid: true, newValue: newValue };
            }),
            int: this.createDataType('int', function (value) {
                var newValue = parseInt(value);
                return { valid: !isNaN(newValue), newValue: newValue };
            }),
            float: this.createDataType('float', function (value) {
                var newValue = parseFloat(value);
                return { valid: !isNaN(newValue), newValue: newValue };
            }),
            email: this.createDataType('email', function (value) {
                var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return { valid: re.test(value), newValue: value };
            }),
            nip: this.createDataType('nip', function (value) {
                return { valid: validateNip(value), newValue: value };
            }),
        };
        this.pushTypeError = this.pushTypeError.bind(this);
        this.createDataType = this.createDataType.bind(this);
        this.addDataType = this.addDataType.bind(this);
        this.validate = this.validate.bind(this);
    }
    Validator.prototype.pushTypeError = function (errors, property, validator) {
        var options = validator.options ? Object.keys(validator.options) : [];
        var name = property.name, parentPath = property.parentPath;
        var optionString = options.length ? "( ".concat(options.join(', '), " )") : '';
        errors.push("Property '".concat(parentPath ? parentPath + ' ' : '').concat(name, "' does not match type '").concat(validator.name, "' ").concat(optionString));
    };
    Validator.prototype.createEmptyDataError = function (parrentPath) {
        var message = parrentPath ? "Property ".concat(parrentPath, " is undefined") : 'Data is empty, but expected object';
        return { valid: false, errors: [message], output: null };
    };
    Validator.prototype.addDataType = function (name, validate) {
        this.dataTypes[name] = this.createDataType(name, validate);
    };
    Validator.prototype.createDataType = function (name, validate) {
        var _this = this;
        return function (options, functionalTypesOptions) {
            if (options === void 0) { options = {}; }
            if (functionalTypesOptions === void 0) { functionalTypesOptions = {}; }
            return {
                name: name,
                options: options,
                functionalTypesOptions: functionalTypesOptions,
                validate: function (value) {
                    var valid = true;
                    if (options) {
                        if (!options.isRequired && !value)
                            return { valid: true };
                        Object.keys(options).forEach(function (o) {
                            var param = options[o];
                            valid = _this.dataOptions[o](value, param);
                        });
                    }
                    if (valid) {
                        return validate(value);
                    }
                    return { valid: false, newValue: value };
                },
            };
        };
    };
    Validator.prototype.validate = function (data, definitions, parentPath) {
        var _this = this;
        if (parentPath === void 0) { parentPath = ''; }
        if (!data)
            return this.createEmptyDataError(parentPath);
        var errors = [];
        var output = {};
        Object.keys(definitions).forEach(function (k) {
            var value = data[k];
            var validator = definitions[k];
            switch (validator.name) {
                case 'shape': {
                    if (!value) {
                        if (validator.functionalTypesOptions.isRequired)
                            errors.push("".concat(parentPath, " ").concat(k, " is missing"));
                    }
                    else {
                        var innerValidationResult = _this.validate(value, validator.options, "".concat(parentPath, " ").concat(k, " >"));
                        errors = errors.concat(innerValidationResult.errors);
                    }
                    break;
                }
                case 'arrayOf': {
                    if (!value) {
                        if (validator.functionalTypesOptions.isRequired)
                            errors.push("".concat(parentPath, " ").concat(k, " is missing"));
                    }
                    else if (Array.isArray(value)) {
                        var innerValidationResults = value.map(function (item, i) {
                            return _this.validate({ item: item }, { item: validator.options }, "".concat(parentPath, " ").concat(k, "(").concat(i, ") >"));
                        });
                        output[k] = [];
                        innerValidationResults.forEach(function (result, i) {
                            output[k][i] = result.output.item;
                            errors = errors.concat(result.errors);
                        });
                    }
                    else {
                        _this.pushTypeError(errors, { name: k, parentPath: parentPath }, validator);
                    }
                    break;
                }
                case 'oneOfType': {
                    var typeValidations = validator.options.map(function (o) {
                        var _a, _b;
                        return _this.validate((_a = {}, _a[k] = value, _a), (_b = {}, _b[k] = o, _b), "".concat(parentPath, " ").concat(k, " >"));
                    });
                    var validTypes = typeValidations.filter(function (v) { return v.valid; });
                    if (validTypes.length) {
                        output[k] = validTypes[0].output[k];
                    }
                    else {
                        if (validator.functionalTypesOptions.isRequired)
                            errors.push("".concat(parentPath, " ").concat(k, " is not valid for any of types ").concat(validator.options.map(function (o) { return o.name; })));
                    }
                    break;
                }
                default: {
                    var result = validator.validate(value);
                    output[k] = result.newValue;
                    if (!result.valid) {
                        _this.pushTypeError(errors, { name: k, parentPath: parentPath }, validator);
                    }
                }
            }
        });
        if (errors.length && parentPath === '')
            throw ClientErrorUtils.createClientError(400, { errors: errors });
        return { valid: errors.length === 0, errors: errors, output: output };
    };
    return Validator;
}());
exports.Validator = Validator;
